import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {View, ScrollView, Text, TouchableOpacity, ActivityIndicator, Alert, Linking, Platform} from 'react-native';
import {
  SettingsHelpIcon,
  SettingsLogoutIcon,
  SettingsNotificationIcon,
  SettingsPasswordIcon,
  SettingsPersonIcon,
  LanguageIcon,
  SettingsEditInterests,
  SettingsThemeIcon,
  EyeIcon,
  RefreshIcon,
  ShareIcon,
  StarIcon,
} from '~assets/icons';
import {useTranslation} from 'react-i18next';
import {LanguagesModal} from '~components/ModalWithItems/LanguagesModal';
import ThemeModal from '~components/ModalWithItems/ThemeModal';
import ModernCard from '~components/ModernCard/ModernCard';
import Animated, {FadeInLeft, FadeInDown} from 'react-native-reanimated';
import createStyles from './styles';
import {spacing} from '~constants/design';
import {OptionItemProps} from '~types/settings/settings.type';
import {OptionItem, ProfileImageComponent, SocialPulseScore} from '~components/Settings';
import SettingsSubscriptionCard from '~components/Settings/SettingsSubscriptionCard';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {NavigationProps} from '~types/navigation/navigation.type';
import {SCREENS} from '~constants';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {useGetUserAccount} from '~hooks/user/useGetUser';
import {useUserStore} from '~providers/userStore/zustand';
import {useUpdateUser} from '~hooks/user/useUpdateUser';
// Import OneSignal stub for web compatibility
import * as oneSignalStub from '~services/web/oneSignalStub';
import {useNotifications} from '~containers/Core/navigation/SettingsStack/zustand';
import {useGetUserType} from '~hooks/event/useGetUserType';
import {useGetBusinessAccount} from '~hooks/business/useGetBusinessAccount';
// Conditionally import OneSignal for native platforms
let OneSignal: any = null;
if (Platform.OS !== 'web') {
  OneSignal = require('react-native-onesignal').default;
} else {
  OneSignal = oneSignalStub.OneSignal;
}
// DeviceInfo will automatically use the stub on web due to webpack alias
import DeviceInfo from 'react-native-device-info';
import useUpdateBusiness from '~hooks/business/useUpdateBusiness';
import {logScreenView} from '~Utils/firebaseAnalytics';
import {useTheme} from '~contexts/ThemeContext';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useGetPyxiSelectAllData} from '~hooks/pyxiselect/useGetPyxiSelectAllData';
import {useGetEventStatistics} from '~hooks/event/useGetEventStatistics';
import {EventStatistics} from '~types/api/eventStatistics';
import {useGetActivePlan} from '~hooks/subscription/useGetActivePlan';
import {useCancelSubscription} from '~hooks/subscription/useCancelSubscription';
import {useGetAffectedOrders} from '~hooks/subscription/useGetAffectedOrders';
import {AffectedOrdersModal} from '~components/ModalWithItems';
import {AffectedOrder} from '~types/subscription/subscription.types';
import {useGetSocialPulse} from '~hooks/user/useGetSocialPulse';
import moment from 'moment';

const SettingsView = () => {
  const {colors, themeMode} = useTheme();
  const styles = createStyles(colors);
  const {
    i18n: {language},
    t,
  } = useTranslation();
  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false);
  const [isThemeModalVisible, setIsThemeModalVisible] = useState(false);
  const navigation = useNavigation<NavigationProps>();
  const {setIsTabBarDisabled} = useTabBar();
  const {setIsNotificationsEnabled} = useNotifications();
  const user = useUserStore(state => state.user);
  const {data: userType} = useGetUserType(user?.uid!);
  const {data: businessAccountData} = useGetBusinessAccount(user?.uid || '');
  const {data: userAccountData} = useGetUserAccount(user?.uid || '');
  const {data: pyxiSelectData, isLoading: isPyxiSelectLoading, error: pyxiSelectError, refetch: refetchPyxiSelectData} = useGetPyxiSelectAllData();
  const {data: eventStatistics, isLoading: isEventStatsLoading, refetch: refetchEventStatistics} = useGetEventStatistics();
  const {data: activePlan, refetch: refetchActivePlan} = useGetActivePlan();
  const {data: socialPulseData, isLoading: isSocialPulseLoading, refetch: refetchSocialPulse} = useGetSocialPulse();
  const {mutate: cancelSubscription, isLoading: isCancelling} = useCancelSubscription();
  const {mutate: getAffectedOrders, isLoading: isFetchingAffectedOrders} = useGetAffectedOrders();
  const [isAffectedOrdersModalVisible, setIsAffectedOrdersModalVisible] = useState(false);
  const [affectedOrders, setAffectedOrders] = useState<AffectedOrder[]>([]);
  const appVersion = DeviceInfo.getVersion();

  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    eventStats: false,
    quizzes: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Social Pulse Score Data from API
  const socialPulseInfo = useMemo(() => {
    if (!socialPulseData) return null;
    return {
      score: socialPulseData.final_score,
      breakdown: {
        attendance: socialPulseData.attendance_points,
        engagement: socialPulseData.engagement_points,
        streak: socialPulseData.streak_bonus,
        negative: socialPulseData.negative_behaviour_points,
      },
      lastUpdated: moment(socialPulseData.updated_at).format('dddd h a [GMT]')
    };
  }, [socialPulseData]);

  const {mutateAsync: updateUserMutation} = useUpdateUser();
  const {mutateAsync: updateBusinessMutation} = useUpdateBusiness();

   useFocusEffect(
      React.useCallback(() => {
        refetchPyxiSelectData();
        refetchEventStatistics();
        refetchActivePlan();
        refetchSocialPulse();
      }, [refetchPyxiSelectData, refetchEventStatistics, refetchActivePlan, refetchSocialPulse]),
    );
  

  const handleChangeUserImage = useCallback(
    async (value: string) => {
      const payload = {uid: user?.uid, photo: value};
      try {
        if (businessAccountData?.uid) {
          updateBusinessMutation(payload);
          return;
        }

        await updateUserMutation(payload);
      } catch (error) {
        console.log(error);
      }
    },
    [businessAccountData?.uid, updateBusinessMutation, updateUserMutation],
  );

  const handleCancelSubscription = () => {
    getAffectedOrders(undefined, {
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setAffectedOrders(data);
          setIsAffectedOrdersModalVisible(true);
        } else {
          Alert.alert(
            'Cancel Subscription',
            'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.',
            [
              {
                text: 'Keep Subscription',
                style: 'cancel',
              },
              {
                text: 'Cancel Subscription',
                onPress: executeCancelSubscription,
                style: 'destructive',
              },
            ],
          );
        }
      },
      onError: () => {
        // Fallback to standard confirmation if API fails
        Alert.alert(
          'Cancel Subscription',
          'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.',
          [
            {
              text: 'Keep Subscription',
              style: 'cancel',
            },
            {
              text: 'Cancel Subscription',
              onPress: executeCancelSubscription,
              style: 'destructive',
            },
          ],
        );
      },
    });
  };

  const executeCancelSubscription = () => {
    cancelSubscription(undefined, {
      onSuccess: () => {
        setIsAffectedOrdersModalVisible(false);
        Alert.alert(
          `We're sorry to see you go but we hope you'll join us again soon!🧡`,
          ``,
          [{text: 'OK', onPress: () => refetchActivePlan()}],
        );
      },
      onError: () => {
        Alert.alert(
          'Error',
          'Failed to cancel subscription. Please try again.',
        );
      },
    });
  };

  const isBusinessAccount = userType === 'business';
  const data: any = isBusinessAccount ? businessAccountData : userAccountData;

  // Transform API data to journey items
  const pyxiSelectJourney = useMemo(() => {
    if (!pyxiSelectData || isPyxiSelectLoading) return [];

    const calculateCompletionPercentage = (filled: string[], total: string[]) => {
      if (total.length === 0) return 100;
      return Math.round((filled.length / total.length) * 100);
    };

    const getUpdateText = (percentage: number) => {
      if (percentage === 100) return 'Completed';
      if (percentage > 50) return 'Nearly complete';
      if (percentage > 0) return 'In progress';
      return 'Not started';
    };

    const journeyItems = [];

    // Dinner Quiz (User + General + Reset Table data)
    const dinnerQuizFilled = [
      ...pyxiSelectData.user.status.filled,
      ...pyxiSelectData.general.status.filled,
      ...pyxiSelectData.reset_table.status.filled,
    ];
    const dinnerQuizTotal = [
      ...pyxiSelectData.user.status.filled,
      ...pyxiSelectData.user.status.not_filled,
      ...pyxiSelectData.general.status.filled,
      ...pyxiSelectData.general.status.not_filled,
      ...pyxiSelectData.reset_table.status.filled,
      ...pyxiSelectData.reset_table.status.not_filled,
    ];
    const dinnerQuizPct = calculateCompletionPercentage(dinnerQuizFilled, dinnerQuizTotal);
    
    journeyItems.push({
      title: 'Dinner Quiz',
      subtitle: 'Core personality insights',
      pct: dinnerQuizPct,
      updated: getUpdateText(dinnerQuizPct),
      icon: '🧠',
    });

    // Culture Edit
    const cultureEditFilled = pyxiSelectData.cultural_edit.status.filled;
    const cultureEditTotal = [
      ...pyxiSelectData.cultural_edit.status.filled,
      ...pyxiSelectData.cultural_edit.status.not_filled,
    ];
    const cultureEditPct = calculateCompletionPercentage(cultureEditFilled, cultureEditTotal);
    
    journeyItems.push({
      title: 'Culture Edit',
      subtitle: 'Cultural preferences & background',
      pct: cultureEditPct,
      updated: getUpdateText(cultureEditPct),
      icon: '🎭',
    });

    // Neighbourhood Match
    const neighborhoodFilled = pyxiSelectData.neighborhood.status.filled;
    const neighborhoodTotal = [
      ...pyxiSelectData.neighborhood.status.filled,
      ...pyxiSelectData.neighborhood.status.not_filled,
    ];
    const neighborhoodPct = calculateCompletionPercentage(neighborhoodFilled, neighborhoodTotal);
    
    journeyItems.push({
      title: 'My Neighbourhood',
      subtitle: 'Local community preferences',
      pct: neighborhoodPct,
      updated: getUpdateText(neighborhoodPct),
      icon: '🏘️',
    });

    // Housewarming Quiz (User + General + Housewarming data)
    const housewarmingQuizFilled = [
      ...pyxiSelectData.user.status.filled,
      ...pyxiSelectData.general.status.filled,
      ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.status.filled : []),
    ];
    const housewarmingQuizTotal = [
      ...pyxiSelectData.user.status.filled,
      ...pyxiSelectData.user.status.not_filled,
      ...pyxiSelectData.general.status.filled,
      ...pyxiSelectData.general.status.not_filled,
      ...(pyxiSelectData.housewarming ? [
        ...pyxiSelectData.housewarming.status.filled,
        ...pyxiSelectData.housewarming.status.not_filled,
      ] : []),
    ];
    const housewarmingQuizPct = calculateCompletionPercentage(housewarmingQuizFilled, housewarmingQuizTotal);
    
    journeyItems.push({
      title: 'Housewarming Quiz',
      subtitle: 'Party planning & hosting preferences',
      pct: housewarmingQuizPct,
      updated: getUpdateText(housewarmingQuizPct),
      icon: '🏠',
    });

    return journeyItems;
  }, [pyxiSelectData, isPyxiSelectLoading, pyxiSelectError]);

  // Insights for collapsed views
  const quizInsights = useMemo(() => {
    if (pyxiSelectJourney.length === 0) return null;
    const total = pyxiSelectJourney.length;
    const avgPct = Math.round(
      pyxiSelectJourney.reduce((acc, curr) => acc + curr.pct, 0) / total
    );
    return { total, avgPct };
  }, [pyxiSelectJourney]);

  const eventInsights = useMemo(() => {
    if (!eventStatistics) return null;
    return {
      joined: eventStatistics.total.events_joined_count,
      met: eventStatistics.total.total_people_met
    };
  }, [eventStatistics]);

  const settings: OptionItemProps[] = useMemo(
    () => [
      {
        name: t('generic.language'),
        id: 'language',
        icon: LanguageIcon,
        subName: language,
        callback: () => setIsLanguagesModalVisible(true),
      },
      {
        name: t('settings.appearance') || 'Appearance',
        id: 'theme',
        icon: SettingsThemeIcon,
        subName:
          themeMode === 'system'
            ? t('settings.theme_system') || 'System'
            : themeMode === 'dark'
              ? t('settings.theme_dark') || 'Dark'
              : t('settings.theme_light') || 'Light',
        callback: () => setIsThemeModalVisible(true),
      },
      {
        name: t('settings.personal_info') ? t('settings.personal_info') : 'Personal Info',
        id: 'person',
        icon: SettingsPersonIcon,
        callback: () => {
          setIsTabBarDisabled(true);
          navigation.navigate(isBusinessAccount ? SCREENS.CHANGE_PROFILE_INFO_BUSINESS : SCREENS.CHANGE_PROFILE_INFO);
        },
      },
      {
        name: t('settings.edit_interests'),
        id: 'subcategories',
        icon: SettingsEditInterests,
        callback: () => {
          setIsTabBarDisabled(true);
          navigation.navigate(SCREENS.EDIT_SUBCATEGORIES);
        },
      },
      /* {
        name: t('settings.edit_preferance'),
        id: 'preferance',
        icon: SettingsEditPreferences,
        callback: () => {
          setIsTabBarDisabled(true);
          navigation.navigate(SCREENS.EDIT_PREFERANCE, {setting: true});
        },
      },*/
      {
        name: t('settings.purchase_history'),
        id: 'purchase_history',
        icon: require('~assets/icons/store.png'),
        callback: () => {
          setIsTabBarDisabled(true);
          navigation.navigate(SCREENS.PURCHASE_HISTORY);
        },
      },
      {
        name: t('settings.change_pwd') ? t('settings.change_pwd') : 'Change Password',
        id: 'lock',
        icon: SettingsPasswordIcon,
        callback: () => {
          setIsTabBarDisabled(true);
          navigation.navigate(SCREENS.CHANGE_PASSWORD);
        },
      },
      {
        name: t('settings.notifications') ? t('settings.notifications') : 'Notifications',
        id: 'notifications',
        icon: SettingsNotificationIcon,
        callback: () => {},
        isSwitch: true,
      },
      {
        name: t('events.neighbourhood_gropus'),
        id: 'groups',
        icon: require('~assets/icons/groups.png'),
        callback: () => {
          setIsTabBarDisabled(true);
          navigation.navigate(SCREENS.GROUPS);
        },
      },
      {
        name: t('settings.help_center') ? t('settings.help_center') : 'Help Center',
        id: 'info',
        icon: SettingsHelpIcon,
        callback: () => {
          setIsTabBarDisabled(true);

          navigation.navigate(SCREENS.HELP_CENTER);
        },
      },
      {
        name: t('settings.follow_pyxi') || 'Follow PYXI',
        id: 'follow_pyxi',
        icon: () => <ShareIcon color={colors.textPrimary} />,
        callback: () => {
          Linking.openURL('https://www.instagram.com/pyxi.ai/');
        },
      },
      {
        name: t('settings.rate_app') || 'Rate The App',
        id: 'rate_app',
        icon: () => <StarIcon color={colors.textPrimary} />,
        callback: () => {
          const url = Platform.OS === 'ios'
            ? 'https://apps.apple.com/gb/app/pyxi/id1663673322'
            : 'market://details?id=com.pyxida.pyxida';
          Linking.openURL(url).catch(() => {
            if (Platform.OS === 'android') {
              Linking.openURL('https://play.google.com/store/apps/details?id=com.pyxida.pyxida');
            }
          });
        },
      },
      {
        name: t('settings.sign_out') ? t('settings.sign_out') : 'Log out',
        id: 'logout',
        icon: SettingsLogoutIcon,
        callback: () => {
          setIsTabBarDisabled(true);

          navigation.navigate(SCREENS.LOGOUT);
        },
      },
    ],
    [language, themeMode, setIsLanguagesModalVisible, setIsThemeModalVisible, t, navigation, colors],
  );

  useLayoutEffect(() => {
    navigation.addListener('focus', () => {
      setIsTabBarDisabled(false);
    });
  }, []);

  useEffect(() => {
    logScreenView('Settings', 'SettingsView');
  }, []);

  useLayoutEffect(() => {
    (async () => {
      const state = await OneSignal.getDeviceState();
      setIsNotificationsEnabled(!state?.isPushDisabled);
    })();
  }, [setIsNotificationsEnabled]);

  return (
    <SafeAreaView edges={['top']} style={styles.container}>
      <ScrollView
        style={styles.background}
        contentContainerStyle={styles.scrollViewContainer}
        showsVerticalScrollIndicator={false}>
        {/* Profile Section */}
        <Animated.View entering={FadeInDown.delay(100).duration(400)}>
          <ModernCard variant="elevated" padding="md" margin="md">
            <ProfileImageComponent
              onChange={handleChangeUserImage}
              value={data?.photo}
              name={isBusinessAccount ? data?.name : `${data?.first_name} ${data?.last_name}`}
              email={data?.email}
            />
          </ModernCard>
        </Animated.View>

        {/* Premium Subscriptions Card */}
        <SettingsSubscriptionCard delay={250} />

        {/* Social Pulse Score Card */}
        {!isBusinessAccount && (
          isSocialPulseLoading ? (
            <ModernCard variant="elevated" padding="lg" margin="md">
              <ActivityIndicator color={colors.primary} />
            </ModernCard>
          ) : socialPulseInfo ? (
            <SocialPulseScore 
              score={socialPulseInfo.score}
              breakdown={socialPulseInfo.breakdown}
              lastUpdated={socialPulseInfo.lastUpdated}
            />
          ) : null
        )}

        {/* Event Statistics Dashboard */}
        <Animated.View entering={FadeInDown.delay(400).duration(400)}>
          <ModernCard 
            variant="elevated" 
            padding="lg" 
            margin="md"
            onPress={() => toggleSection('eventStats')}
          >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: expandedSections.eventStats ? spacing.lg : 0 }}>
              <Text style={[styles.eventStatsTitle, { marginBottom: 0 }]}>
                {'\u{1F3C6}  '}Event Activities
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {!expandedSections.eventStats && eventInsights && (
                  <View style={styles.headerInsightContainer}>
                    <View style={styles.headerInsightChip}>
                      <Text style={styles.headerInsightText}>{eventInsights.joined} Joined</Text>
                    </View>
                    <View style={styles.headerInsightChipSecondary}>
                      <Text style={styles.headerInsightTextSecondary}>{eventInsights.met} Met</Text>
                    </View>
                  </View>
                )}
                <Text style={{ fontSize: 10, color: colors.textSecondary, opacity: 0.5, marginLeft: spacing.sm }}>
                  {expandedSections.eventStats ? '▲' : '▼'}
                </Text>
              </View>
            </View>

            {expandedSections.eventStats && (
              <>
                {isEventStatsLoading ? (
                  <View style={styles.loadingContainer}>
                    <Text style={styles.loadingText}>Loading event statistics...</Text>
                  </View>
                ) : eventStatistics ? (
                  <View>
                    {/* Total Stats */}
                    <View style={styles.eventStatsTotalContainer}>
                      <View style={styles.eventStatCard}>
                        <Text style={styles.eventStatLabel}>Events Joined</Text>
                        <Text style={styles.eventStatValue}>
                          {eventStatistics.total.events_joined_count}
                        </Text>
                      </View>
                      <View style={styles.eventStatCard}>
                        <Text style={styles.eventStatLabel}>People Met</Text>
                        <Text style={styles.eventStatValue}>
                          {eventStatistics.total.total_people_met}
                        </Text>
                      </View>
                    </View>

                    {/* Category Breakdown */}
                    <Text style={styles.eventStatsCategoryTitle}>By Category</Text>
                    
                    {[
                      { key: 'THE_REST_TABLE', label: 'The Reset Table', icon: '✨' },
                      { key: 'CULTURAL_EDIT', label: 'Cultural Edit', icon: '🎭' },
                      { key: 'GENERAL', label: 'Pyxi Core', icon: '👥' },
                      { key: 'NEIGHBORHOOD', label: 'Neighborhood', icon: '🏘️' },
                      { key: 'COUPLES', label: 'Couples', icon: '💑' },
                    ].map((category) => {
                      const stats = eventStatistics[category.key as keyof EventStatistics] as any;
                      return (
                        <View key={category.key} style={styles.eventCategoryRow}>
                          <View style={styles.eventCategoryIconContainer}>
                            <Text style={styles.eventCategoryIcon}>{category.icon}</Text>
                          </View>
                          <View style={styles.eventCategoryContent}>
                            <Text style={styles.eventCategoryLabel}>{category.label}</Text>
                            <View style={styles.eventCategoryStatsRow}>
                              <Text style={styles.eventCategoryStatText}>
                                {stats.events_joined_count} joined
                              </Text>
                              <Text style={styles.eventCategoryStatSeparator}>•</Text>
                              <Text style={styles.eventCategoryStatText}>
                                {stats.total_people_met} people met
                              </Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No event statistics available yet. Start joining events!</Text>
                  </View>
                )}
              </>
            )}
          </ModernCard>
        </Animated.View>


        {/* Pyxi journey */}
        <Animated.View entering={FadeInDown.delay(300).duration(400)}>
          <ModernCard 
            variant="elevated" 
            padding="lg" 
            margin="md"
            onPress={() => toggleSection('quizzes')}
          >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: expandedSections.quizzes ? spacing.lg : 0 }}>
              <Text style={[styles.pyxiJourneyTitle, { marginBottom: 0 }]}>
                {'\u{1F9ED}  '}My Quizzes
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {!expandedSections.quizzes && quizInsights && (
                  <View style={styles.headerInsightContainer}>
                    <View style={styles.headerInsightChip}>
                      <Text style={styles.headerInsightText}>{quizInsights.total} Quizzes</Text>
                    </View>
                    <View style={styles.headerInsightChipSecondary}>
                      <Text style={styles.headerInsightTextSecondary}>{quizInsights.avgPct}% Optimized</Text>
                    </View>
                  </View>
                )}
                <Text style={{ fontSize: 10, color: colors.textSecondary, opacity: 0.5, marginLeft: spacing.sm }}>
                  {expandedSections.quizzes ? '▲' : '▼'}
                </Text>
              </View>
            </View>

            {expandedSections.quizzes && (
              <>
                {isPyxiSelectLoading ? (
                  <View style={styles.loadingContainer}>
                    <Text style={styles.loadingText}>Loading your quizzes...</Text>
                  </View>
                ) : pyxiSelectError ? (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>Unable to load quiz data. Please try again later.</Text>
                  </View>
                ) : pyxiSelectJourney.length === 0 ? (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>Start your Pyxi journey by taking the quiz!</Text>
                  </View>
                ) : (
                  pyxiSelectJourney.map((item, index) => (
                    <View
                      key={`pyxi-journey-${index}`}
                      style={styles.journeyItemContainer}>
                      <View style={styles.journeyItemRow}>
                        <View style={styles.journeyIconContainer}>
                          <Text style={styles.journeyIconText}>{item.icon}</Text>
                        </View>

                        <View style={styles.journeyContentContainer}>
                          <View style={styles.journeyTitleRow}>
                            <View style={styles.journeyTitleContainer}>
                              <Text style={styles.journeyTitle}>
                                {item.title}
                              </Text>
                              <Text style={styles.journeySubtitle}>{item.subtitle}</Text>
                            </View>
                          </View>

                          <View style={styles.journeyProgressRow}>
                            <View style={styles.journeyProgressContainer}>
                              <View style={styles.journeyProgressBar}>
                                <View style={[styles.journeyProgressFill, {width: `${item.pct}%`}]} />
                              </View>
                            </View>
                            <Text style={styles.journeyProgressText}>
                              {item.pct}%
                            </Text>
                          </View>

                          <View style={styles.journeyUpdatedContainer}>
                            <Text style={styles.journeyUpdatedText}>{item.updated}</Text>
                          </View>
                        </View>
                        <View style={styles.journeyActionsContainer}>
                        <TouchableOpacity
                          onPress={() => {
                            setIsTabBarDisabled(true);
                            const formType = 
                              item.title === 'Culture Edit' ? 'cultural_edit' : 
                              item.title === 'My Neighbourhood' ? 'neighborhood' : 
                              item.title === 'Housewarming Quiz' ? 'housewarming' :
                              'reset_table';
                            
                            // Extract field data based on form type (only if pyxiSelectData exists)
                            let fieldData = {};
                            let statusData: { filled: string[]; not_filled: string[] } = { filled: [], not_filled: [] };
                            
                            if (pyxiSelectData) {
                              if (formType === 'reset_table') {
                                // Combine user, general, and reset_table data
                                fieldData = {
                                  ...pyxiSelectData.user.data,
                                  ...pyxiSelectData.general.data,
                                  ...pyxiSelectData.reset_table.data,
                                };
                                statusData = {
                                  filled: [
                                    ...pyxiSelectData.user.status.filled,
                                    ...pyxiSelectData.general.status.filled,
                                    ...pyxiSelectData.reset_table.status.filled,
                                  ],
                                  not_filled: [
                                    ...pyxiSelectData.user.status.not_filled,
                                    ...pyxiSelectData.general.status.not_filled,
                                    ...pyxiSelectData.reset_table.status.not_filled,
                                  ],
                                };
                              } else if (formType === 'cultural_edit') {
                                fieldData = pyxiSelectData.cultural_edit.data;
                                statusData = pyxiSelectData.cultural_edit.status;
                              } else if (formType === 'neighborhood') {
                                fieldData = pyxiSelectData.neighborhood.data;
                                statusData = pyxiSelectData.neighborhood.status;
                              } else if (formType === 'housewarming') {
                                // Combine user, general, and housewarming data
                                fieldData = {
                                  ...pyxiSelectData.user.data,
                                  ...pyxiSelectData.general.data,
                                  ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.data : {}),
                                };
                                statusData = {
                                  filled: [
                                    ...pyxiSelectData.user.status.filled,
                                    ...pyxiSelectData.general.status.filled,
                                    ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.status.filled : []),
                                  ],
                                  not_filled: [
                                    ...pyxiSelectData.user.status.not_filled,
                                    ...pyxiSelectData.general.status.not_filled,
                                    ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.status.not_filled : []),
                                  ],
                                };
                              }
                            }

                            console.log(fieldData, 'fieldDatafieldData');
                            
                            
                            navigation.navigate(SCREENS.PYXI_SELECT_QUIZ as any, { 
                              form_type: formType, 
                              mode: 'view',
                              fieldData: fieldData,
                              statusData: statusData
                            });
                          }}
                          style={styles.journeyViewButton}>
                            <View style={styles.journeyViewButtonRow}>
                              <EyeIcon color={colors.textPrimary} />
                              <Text style={styles.journeyViewButtonText}>
                                View
                              </Text>
                            </View>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => {
                              setIsTabBarDisabled(true);
                              const formType = 
                                item.title === 'Culture Edit' ? 'cultural_edit' : 
                                item.title === 'My Neighbourhood' ? 'neighborhood' : 
                                item.title === 'Housewarming Quiz' ? 'housewarming' :
                                'reset_table';
                              const mode = item.pct === 100 ? 'retake' : 'edit';
                              
                              // Extract field data based on form type (only if pyxiSelectData exists)
                              let fieldData = {};
                              let statusData: { filled: string[]; not_filled: string[] } = { filled: [], not_filled: [] };
                              
                              if (pyxiSelectData) {
                                if (formType === 'reset_table') {
                                  // Combine user, general, and reset_table data
                                  fieldData = {
                                    ...pyxiSelectData.user.data,
                                    ...pyxiSelectData.general.data,
                                    ...pyxiSelectData.reset_table.data,
                                  };
                                  statusData = {
                                    filled: [
                                      ...pyxiSelectData.user.status.filled,
                                      ...pyxiSelectData.general.status.filled,
                                      ...pyxiSelectData.reset_table.status.filled,
                                    ],
                                    not_filled: [
                                      ...pyxiSelectData.user.status.not_filled,
                                      ...pyxiSelectData.general.status.not_filled,
                                      ...pyxiSelectData.reset_table.status.not_filled,
                                    ],
                                  };
                                } else if (formType === 'cultural_edit') {
                                  fieldData = pyxiSelectData.cultural_edit.data;
                                  statusData = pyxiSelectData.cultural_edit.status;
                                } else if (formType === 'neighborhood') {
                                  fieldData = pyxiSelectData.neighborhood.data;
                                  statusData = pyxiSelectData.neighborhood.status;
                                } else if (formType === 'housewarming') {
                                  // Combine user, general, and housewarming data
                                  fieldData = {
                                    ...pyxiSelectData.user.data,
                                    ...pyxiSelectData.general.data,
                                    ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.data : {}),
                                  };
                                  statusData = {
                                    filled: [
                                      ...pyxiSelectData.user.status.filled,
                                      ...pyxiSelectData.general.status.filled,
                                      ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.status.filled : []),
                                    ],
                                    not_filled: [
                                      ...pyxiSelectData.user.status.not_filled,
                                      ...pyxiSelectData.general.status.not_filled,
                                      ...(pyxiSelectData.housewarming ? pyxiSelectData.housewarming.status.not_filled : []),
                                    ],
                                  };
                                }
                              }
                              
                              navigation.navigate(SCREENS.PYXI_SELECT_QUIZ as any, { 
                                form_type: formType, 
                                mode: mode,
                                fieldData: fieldData,
                                statusData: statusData
                              });
                            }}
                            style={styles.journeyRetakeButton}>
                            {item.pct === 100 ? (
                              <RefreshIcon width={18} height={18} color={colors.textPrimary} />
                            ) : (
                              <Text style={{color: colors.textPrimary, fontSize: 16}}>▶</Text>
                            )}
                            <Text style={styles.journeyRetakeButtonText}>
                              {item.pct === 100 ? 'Retake' : item.pct === 0 ? 'Start' : 'Resume'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  ))
                )}
              </>
            )}
          </ModernCard>
        </Animated.View>
        {/* My Personality & Interests */}
        {/* <Animated.View entering={FadeInDown.delay(500).duration(400)}>
          <Text
            style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              marginBottom: spacing.sm,
              marginTop: spacing.lg,
              paddingHorizontal: spacing.md,
            }}>
            {"✨  "}My Personality & Interests
          </Text>

          <ModernCard variant="default" padding="md" margin="sm">
            {[{
              title: 'Cooking & Food',
              subtitle: 'Neighbourhood',
              icon: '🍳',
            }, {
              title: 'Movies & TV',
              subtitle: 'Neighbourhood',
              icon: '🎬',
            }, {
              title: 'Fitness & Sports',
              subtitle: 'Neighbourhood',
              icon: '🏃‍♀️',
            }].map((item, index) => (
              <View key={`interest-${index}`} style={{flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm}}>
                <View style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: colors.border + '20',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: spacing.md,
                }}>
                  <Text style={{fontSize: 18}}>{item.icon}</Text>
                </View>

                <View style={{flex: 1}}>
                  <Text style={{color: colors.textPrimary, fontWeight: typography.fontWeight.bold}}>{item.title}</Text>
                  <Text style={{color: colors.gray400, marginTop: 4}}>{item.subtitle}</Text>
                </View>
              </View>
            ))}

            <View style={{height: 1, backgroundColor: colors.border + '40', marginVertical: spacing.sm}} />

            <Text style={{color: colors.textPrimary, fontWeight: typography.fontWeight.bold, marginBottom: spacing.xs}}>Social Style Insights</Text>
            <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
              <View style={{flex: 1, marginRight: spacing.md}}>
                <View style={{height: 8, backgroundColor: colors.border + '22', borderRadius: 8, overflow: 'hidden'}}>
                  <View style={{width: '50%', height: '100%', backgroundColor: colors.primary || '#FFA500'}} />
                </View>
                <Text style={{color: colors.gray400, marginTop: spacing.xs}}>Social Energy</Text>
              </View>

              <View style={{width: 36, alignItems: 'center'}}>
                <Text style={{color: colors.primary, fontWeight: typography.fontWeight.bold}}>5/10</Text>
              </View>
            </View>
          </ModernCard>
        </Animated.View> */}

        {/* Settings Sections */}
        <View style={styles.settingsContainer}>
          {/* Account Settings */}
          <Animated.View entering={FadeInDown.delay(200).duration(400)}>
            <Text style={styles.sectionTitle}>
              {t('settings.title') || 'Settings'}
            </Text>
            <ModernCard variant="default" padding="xs">
              {settings.slice(0, 8).map((setting, index) => (
                <Animated.View
                  key={`${setting.id}-${index}`}
                  entering={FadeInLeft.delay(300 + 50 * index).duration(300)}>
                  <OptionItem {...setting} />
                  {index < settings.slice(0, 8).length - 1 && (
                    <View style={styles.settingsDivider} />
                  )}
                </Animated.View>
              ))}
            </ModernCard>
          </Animated.View>

          {/* Support & Legal */}
          <Animated.View entering={FadeInDown.delay(600).duration(400)}>
            <Text style={styles.sectionTitleLarge}>
              {t('settings.support') || 'Support & Social'}
            </Text>
            <ModernCard variant="default" padding="xs">
              {settings.slice(8).map((setting, index) => (
                <Animated.View
                  key={`${setting.id}-${index}`}
                  entering={FadeInLeft.delay(700 + 50 * index).duration(300)}>
                  <OptionItem {...setting} />
                  {index < settings.slice(7).length - 1 && (
                    <View style={styles.settingsDivider} />
                  )}
                </Animated.View>
              ))}
            </ModernCard>
          </Animated.View>
        </View>

        {/* Cancel Subscription Button */}
        {activePlan && (
          <TouchableOpacity
            style={styles.cancelSubscriptionButton}
            onPress={handleCancelSubscription}
            disabled={isCancelling || isFetchingAffectedOrders}
            activeOpacity={0.8}>
            {isCancelling || isFetchingAffectedOrders ? (
              <ActivityIndicator color={colors.primary} size="small" />
            ) : (
              <Text style={styles.cancelSubscriptionButtonText}>
                Cancel Subscription
              </Text>
            )}
          </TouchableOpacity>
        )}

        <View style={styles.versionContainer}>
          <Text style={styles.versionText}>Version: {appVersion}</Text>
        </View>
        <LanguagesModal isVisible={isLanguagesModalVisible} onClose={() => setIsLanguagesModalVisible(false)} />
        <ThemeModal isVisible={isThemeModalVisible} onClose={() => setIsThemeModalVisible(false)} />
        <AffectedOrdersModal
          isVisible={isAffectedOrdersModalVisible}
          onClose={() => setIsAffectedOrdersModalVisible(false)}
          onConfirm={executeCancelSubscription}
          affectedOrders={affectedOrders}
          isCancelling={isCancelling}
        />
      </ScrollView>
    </SafeAreaView>
  );
};

export default SettingsView;
